<script>
import EgTabs from "@/components/global/eg_tabs.vue";
import EgButton from "@/components/global/buttons/eg_button.vue";
import FeedbackReportV3 from "@/components/essay/essay/v3/feedback_report_v3.vue";
import GrammarReportV3 from "@/components/essay/essay/v3/grammar_report_v3.vue";
import EssayContent from "@/components/essay/essay/essay_content.vue";
import { createPolling } from "@/utils/polling.js";
import { checkPdfExportStatus } from "@/api/requests/pdfExport.js";

export default {
  name: "FeedbackStep",
  components: {
    EgTabs,
    EgButton,
    FeedbackReportV3,
    GrammarReportV3,
    EssayContent,
  },
  props: {
    essay: {
      type: Object,
      default: null,
    },
  },
  emits: ["reset"],
  data() {
    return {
      registerLink: "https://app.essaygrader.ai/register",
      selectedFeedbackTab: "feedback",
      activeTab: 0,
      regrading: false,
      loading: true,
      error: "",
      pageInfo: null,
      copyError: "",
      showHighlight: true,

      highlights: [],
      activeHighlightIndex: -1,

      polling: null,
      exporting: false,
      exportUrl: null,
      popupBlocked: false,
    };
  },
  beforeDestroy() {
    if (this.polling?.isPolling) {
      this.polling.stop();
    }
  },
  methods: {
    async highlightClicked(highlight) {
      if (!highlight.refComponent) {
        throw new Error(
          "highlight should has refComponent - link to vue component"
        );
      }

      await this.$nextTick();

      highlight.refComponent?.$el?.scrollIntoView({ behavior: "smooth" });
      this.activeHighlightIndex = this.highlights.findIndex(
        (highlightItem) =>
          highlight.refComponent._uid === highlightItem.refComponent._uid
      );
    },

    initPolling(jobId) {
      this.polling = createPolling({
        callback: this.checkExportStatus,
        interval: 2000,
        maxRetries: 30,
        onError: this.handlePollingError,
      });
      this.polling.currentJobId = jobId;
      this.polling.start();
    },

    async checkExportStatus() {
      try {
        const jobStatus = await checkPdfExportStatus(this.polling.currentJobId);
        const { data } = jobStatus;
        if (data.status === "ready") {
          await this.handleExportReady(data);
          return data;
        } else if (data.status === "pending") {
          return data;
        }
      } catch (error) {
        if (this.polling?.isPolling) {
          this.polling.stop();
        }
        throw new Error("PDF export failed");
      }
    },

    async handleExportReady(data) {
      this.exportUrl = data.url;

      // Automatic download
      const link = document.createElement("a");
      link.href = data.url;
      link.download = "essay-feedback.pdf";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.exporting = false;
      if (this.polling?.isPolling) {
        this.polling.stop();
      }
    },

    handlePollingError(error, retryCount) {
      console.error(`Polling error (retry ${retryCount}):`, error);
    },

    handleExportClick() {
      if (!this.essay?.jobId) {
        console.error("No jobId available for PDF export");
        return;
      }
      this.exporting = true;
      this.initPolling(this.essay.jobId);
    },
  },
};
</script>

<template>
  <div class="scroll-container">
    <div>
      <b-row class="content-wrapper h-100">
        <b-col lg="6" class="d-flex flex-column pr-4">
          <div class="mb-3">
            <div class="d-flex flex-column gap-4 pr-4 pl-4 pt-4">
              <b class="submission-heading">Student submission</b>
              <p class="submission-subtitle">
                Paste your essay or upload a file
              </p>
            </div>
          </div>
        </b-col>
        <b-col lg="6" class="d-flex flex-column pl-4">
          <div class="mb-3">
            <div class="d-flex flex-column gap-4 pt-4">
              <b class="submission-heading">Grading Options</b>
              <p class="submission-subtitle">
                Select the grade level, rubric, and grading intensity for this
                essay.
              </p>
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row class="content-wrapper h-100">
        <b-col
          lg="6"
          class="essay-view-details-column d-flex flex-column h-100 overflow-auto"
        >
          <div class="h-100">
            <essay-content
              :essay="essay"
              :comments="[]"
              :comment-loading="false"
              :highlights="highlights"
              :show-highlight="showHighlight"
              :active-highlight-index="activeHighlightIndex"
              @highlightClicked="highlightClicked"
              @updateActiveHighlightIndex="activeHighlightIndex = $event"
            />
          </div>
        </b-col>
        <b-col
          lg="6"
          class="mt-4 mt-lg-0 p-0 essay-view-details-column d-flex flex-column h-100 overflow-auto pr-0"
        >
          <eg-tabs
            v-model="activeTab"
            class="h-100 d-flex flex-column overflow-auto"
            content-class="h-100"
            nav-class-wrapper="mx-3"
            @input="activeHighlightIndex = -1"
          >
            <b-tab title="Feedback report" lazy>
              <feedback-report-v3
                :essay="essay"
                :active-highlight-index="activeHighlightIndex"
                @updateHighlights="highlights = $event"
                @updateActiveHighlightIndex="activeHighlightIndex = $event"
              />
            </b-tab>
            <b-tab title="Grammar report" lazy>
              <grammar-report-v3
                :essay="essay"
                :active-highlight-index="activeHighlightIndex"
                @updateHighlights="highlights = $event"
                @updateActiveHighlightIndex="activeHighlightIndex = $event"
              />
            </b-tab>
            <!-- <b-tab title="AI Detection" :disabled="true" lazy /> -->
          </eg-tabs>
        </b-col>
      </b-row>
    </div>
    <div class="grading-buttons-row p-24 pb-4">
      <div class="d-flex flex-gap-1">
        <eg-button
          v-if="!exportUrl"
          size="lg"
          type="secondary"
          :disabled="!essay?.jobId"
          :loading="exporting"
          @click="handleExportClick"
        >
          Export to PDF
        </eg-button>
        <eg-button
          v-else
          size="lg"
          type="secondary"
          :href="exportUrl"
          target="_blank"
        >
          Download the file
        </eg-button>
        <eg-button size="lg" type="secondary" @click="$emit('reset')">
          Grade new essay
        </eg-button>
      </div>
      <eg-button
        size="lg"
        type="primary"
        :href="registerLink"
        target="_blank"
        class="get-started"
      >
        <span class="">&#x2B50;</span> Get 100 gradings for free
      </eg-button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.scroll-container {
  max-height: calc(100vh - 68px);
  overflow-x: hidden;
  overflow-y: auto;
  background: white;
  border-radius: 16px;
}
.grading-buttons-row {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  justify-content: space-between;
  padding-bottom: 0;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  border-bottom-right-radius: 16px;
  border-bottom-left-radius: 16px;
  .eg-new-button {
    max-height: 36px;
  }
}
.step-3-layout {
  display: grid;

  grid-template: auto minmax(auto, 600px) / 1fr 1fr;
  grid-auto-flow: column;
  grid-gap: 1rem;
}

@media (max-width: 900px) {
  .step-3-layout {
    grid-template: auto minmax(auto, 400px) auto minmax(auto, 400px) / auto;
  }
}

.link-button {
  color: #6a50f3;
  &:focus {
    text-decoration: none;
    box-shadow: none;
  }
}

.preserve-newlines {
  white-space: pre-wrap;
}
</style>
